import { useCallback, useMemo, useRef } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import { useArrivalChannel } from './useArrivalChannel';
import type { ChannelAwareCtaLayout } from './useChannelAwareCta';

const WHATSAPP_ARRIVAL = 'whatsapp';

type WhatsappCtaVariation = PsfPromoteOrSharePageData['psfPromoteOrShareWhatsappCtaExperiment']['variation'];

// Keyed on the config's own variations union so renaming an arm fails to compile instead of silently
// rendering control. `control`/`fake_control` are deliberately absent: they map to `null` below and
// render the production sheet exactly like a non-enrolled signer. Mapping matches the Facebook hook so
// the two channels' readouts stay comparable.
const ARM_LAYOUTS: Readonly<Partial<Record<WhatsappCtaVariation, ChannelAwareCtaLayout>>> = {
	variant_1: 'cta_with_secondary_copy',
	variant_2: 'cta_only',
};

/**
 * CHANGE-99290 (channel-aware CTA, WhatsApp Top-8 relaunch): treats + fires `$exposure` the moment a
 * WhatsApp-arrival signer opens the share slide-up, so the treated population equals the exposed
 * population equals WhatsApp slide-up openers — and maps the treated arm to the share-sheet layout it
 * renders. Re-tests both designs (control vs. `variant_1`/`variant_2`) rather than assuming the
 * concluded English result (CHANGE-98145) transfers, and runs on the same shared "Share on {Platform}"
 * copy as the Facebook relaunch so the two readouts are directly comparable.
 *
 * Countries already served by the productized `psf_promote_or_share_channel_aware_cta` are excluded
 * from enrollment outright — same reasoning as `useTreatFacebookCtaIntlExperiment`: enabling this
 * experiment's FCM for such a country would put both arms on identical pixels. That FCM is empty
 * everywhere for the relaunch, so today this excludes nobody.
 *
 * No explicit mobile-only check: the slide-up trigger this hook is driven from is itself mobile-gated
 * (`md:hidden`), so enrollment is already restricted to that surface.
 */
export function useTreatWhatsappCtaExperiment(): {
	treatOnSlideUpOpen: () => Promise<void>;
	whatsappCtaLayout: ChannelAwareCtaLayout | null;
} {
	const {
		data: { psfPromoteOrShareWhatsappCtaExperiment: experiment, channelAwareCtaChannels },
	} = usePsfPromoteOrSharePageData();
	const arrivalChannel = useArrivalChannel();
	const utilityContext = useUtilityContext();
	const treatPromiseRef = useRef<Promise<void> | null>(null);

	// treatExperiment bypasses the config's enabledIf, so gate on the FCM flag here too — this is what
	// keeps the experiment a no-op while the kill-switch is off.
	// `!fallback` keeps treated == exposed: useTrackExperimentExposure refuses to fire $exposure on a
	// fallback resolution (kill-switch, error, bot, invalid name), so treating one would enroll a session
	// that can never expose. It already subsumes the sentinel name below, which is kept as belt and braces.
	const isEligible =
		arrivalChannel === WHATSAPP_ARRIVAL &&
		!channelAwareCtaChannels.includes(WHATSAPP_ARRIVAL) &&
		experiment.config.enabled &&
		experiment.experimentName !== '__invalid_experiment__' &&
		!experiment.fallback;

	// treatExperiment registers treatment server-side, but the resolved object stays treated:false, and
	// useTrackExperimentExposure no-ops on !treated — so the exposure object is armed with treated:true
	// from mount. Safe only because the sole call site below gates on treatExperiment's own return
	// (server-confirmed registration). Do not call trackExposure from anywhere else.
	const experimentForExposure = useMemo(() => ({ ...experiment, treated: true }), [experiment]);
	const trackExposure = useTrackExperimentExposure(experimentForExposure);

	const treatOnSlideUpOpen = useCallback(async () => {
		if (!isEligible) return undefined;
		// One-way latch holding the in-flight promise, never reset: React state is stale within the same
		// tick, and a `finally`-released ref is back to null by the time the signer reopens the sheet —
		// both leak a second treat, double-counting one signer.
		// eslint-disable-next-line react-hooks/refs -- read/written synchronously on the tap, never during render.
		treatPromiseRef.current ??= (async () => {
			// `treatExperiment` resolves `false` rather than throwing when the treatment did not land
			// server-side (network failure, bot session, not-yet-treatable assignment), so $exposure is gated
			// on that return: firing it for an unregistered treat would expose a session
			// experiment_variations has no record of.
			//
			// Dispatched here rather than from an effect keyed on a `treated` state flag: an effect only runs
			// on the next commit, so a signer who taps Continue straight after opening the sheet can unmount
			// the route first and lose the exposure entirely.
			if (await treatExperiment(utilityContext, experiment.experimentName)) {
				await trackExposure().catch(() => undefined);
			}
		})();
		// eslint-disable-next-line react-hooks/refs -- same synchronous tap-time access as the write above.
		return treatPromiseRef.current;
	}, [isEligible, experiment.experimentName, utilityContext, trackExposure]);

	// Shares the treat's eligibility gate, so a treatment layout only ever renders for a signer this
	// hook would enroll on open. The converse isn't guaranteed: a failed treatExperiment (network drop)
	// leaves a signer seeing the treatment with no experiment_variations row — the same accepted
	// tradeoff as every treat-on-action sibling, and $exposure stays gated on the confirmed treat.
	const whatsappCtaLayout = (isEligible && ARM_LAYOUTS[experiment.variation]) || null;

	return { treatOnSlideUpOpen, whatsappCtaLayout };
}
